import './index.scss';

import { createPortal } from 'react-dom';
import { FunctionComponent, JSXElementConstructor, ReactElement, ReactNode, Ref, RefObject, useContext, useRef, useState } from 'react';
import { autoUpdate, arrow, offset, Placement, useFloating, FloatingArrow, shift, flip, useHover, useInteractions } from '@floating-ui/react';

import ModalsContext from 'components/BraxModals/context';

export type BraxTooltipInstance = {
    update: () => void
};

export type BraxTooltipPlacement = Placement;

export type BraxTooltipProps = {
    open?: boolean,
    image?: string,
    title: ReactNode,
    description: ReactNode,
    placement?: BraxTooltipPlacement,
    className?: string,
    instance?: RefObject<BraxTooltipInstance>,
    children: ReactElement<any, string | JSXElementConstructor<any>> | ((props: { ref: Ref<HTMLElement> } & Record<string, unknown>) => ReactNode)
} | {
    open?: boolean,
    tooltip: ReactNode,
    placement?: BraxTooltipPlacement,
    className?: string,
    instance?: RefObject<BraxTooltipInstance>,
    children: ReactElement<any, string | JSXElementConstructor<any>> | ((props: { ref: Ref<HTMLElement> } & Record<string, unknown>) => ReactNode)
};

export const BraxTooltip: FunctionComponent<BraxTooltipProps> = (props) => {
    const [open, setOpen] = useState(false);

    const isOpen = props.open !== undefined ? props.open : open;

    const modals = useContext(ModalsContext);

    const arrowRef = useRef<SVGSVGElement>(null);

    const { refs, floatingStyles, context, update } = useFloating({
        open: open,
        placement: props.placement,
        whileElementsMounted: autoUpdate,
        onOpenChange: setOpen,
        middleware: [
            flip(),
            offset(16),
            shift(),
            arrow({ element: arrowRef })
        ]
    });

    const hover = useHover(context, { enabled: props.open === undefined, delay: { close: 500 } });

    const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

    if (props.instance)
        // @ts-ignore
        props.instance.current = { update };

    return (
        <div className={`brax-tooltip ${isOpen ? 'open' : 'close'}`}>
            { typeof props.children === 'function' ? (
                <div>{ props.children({ ref: refs.setReference, ...getReferenceProps() }) }</div>
            ) : (
                <div ref={refs.setReference} {...getReferenceProps()}>{ props.children }</div>
            ) }

            { modals.ref.current ? createPortal(
                <div ref={refs.setFloating} className={`brax-tooltip-content ${isOpen ? 'open' : 'close'} ${props.className || ''}`} style={floatingStyles} {...getFloatingProps()}>
                    <FloatingArrow ref={arrowRef} context={context} width={32} height={16} />

                    <div>
                        { 'tooltip' in props ? (<>
                            <div>
                                { props.tooltip }
                            </div>
                        </>) : (<>
                            { props.image ? <img src={props.image} alt="" /> : null }

                            <div>
                                <p>
                                    <strong>{ props.title }</strong>
                                </p>

                                <p>{ props.description }</p>
                            </div>
                        </>) }
                    </div>
                </div>,
                modals.ref.current
            ) : null }
        </div>
    );
}
